import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { handleError, handleSuccess } from '../utils';
import './Profile.css';

function Profile() {
    const [profileData, setProfileData] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({
        phone: '',
        telegram: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();

    // ✅ Fetch user profile on mount
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/me`, {
                    headers: {
                        'Authorization': token
                    }
                });

                const result = await response.json();

                if (result.success) {
                    setProfileData(result.user);
                    setFormData({
                        phone: result.user.phone || '',
                        telegram: result.user.telegram || ''
                    });
                } else {
                    handleError(result.message || 'Failed to fetch profile');
                    navigate('/login');
                }
            } catch (error) {
                console.error('Error fetching profile:', error);
                handleError('Error loading profile');
                navigate('/login');
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/auth/complete-profile`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': token
                    },
                    body: JSON.stringify({
                        gender: profileData.gender,
                        phone: formData.phone,
                        telegram: formData.telegram
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                localStorage.setItem('token', result.jwtToken);
                setProfileData(prev => ({
                    ...prev,
                    phone: result.user.phone,
                    telegram: result.user.telegram
                }));
                setEditMode(false);
                handleSuccess('Profile updated successfully!');
            } else {
                handleError(result.message || 'Failed to update profile');
            }
        } catch (err) {
            console.error('Error:', err);
            handleError('Error updating profile');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="ui-page">
                <div className="ui-container">
                    <div className="ui-card">
                        <div className="ui-card-body">
                            <p className="ui-subtitle" style={{ margin: 0 }}>Loading profile…</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="ui-page">
            <div className="ui-container" style={{ maxWidth: 720 }}>
                <div className="ui-header" style={{ justifyContent: 'center', textAlign: 'center' }}>
                    <div>
                        <h1 className="ui-title">My profile</h1>
                        <p className="ui-subtitle">View and manage your account details.</p>
                    </div>
                </div>

                <div className="ui-card">
                    <div className="ui-card-body">
                        <div className="ui-stack" style={{ gap: '1rem' }}>
                            <div className="ui-field">
                                <div className="ui-label">Name</div>
                                <div className="ui-readonly">{profileData?.name}</div>
                            </div>

                            <div className="ui-field">
                                <div className="ui-label">Email (IRIS)</div>
                                <div className="ui-readonly">{profileData?.email}</div>
                            </div>

                            <div className="ui-field">
                                <div className="ui-label">Gender</div>
                                <div className="ui-readonly">{profileData?.gender || '—'}</div>
                            </div>

                            <div className="ui-divider" />

                            {editMode ? (
                                <form onSubmit={handleSave} className="ui-stack" style={{ gap: '0.9rem' }}>
                                    <div className="ui-field">
                                        <label className="ui-label" htmlFor="phone">Phone (optional)</label>
                                        <input
                                            id="phone"
                                            type='tel'
                                            name='phone'
                                            value={formData.phone}
                                            onChange={handleChange}
                                            placeholder='e.g. +91 9876543210'
                                            className="ui-input"
                                        />
                                    </div>

                                    <div className="ui-field">
                                        <label className="ui-label" htmlFor="telegram">Telegram username (optional)</label>
                                        <input
                                            id="telegram"
                                            type='text'
                                            name='telegram'
                                            value={formData.telegram}
                                            onChange={handleChange}
                                            placeholder='e.g. johndoe123'
                                            className="ui-input"
                                        />
                                    </div>

                                    <div className="ui-actions">
                                        <button type='submit' disabled={saving} className="ui-btn ui-btn-primary ui-btn-block">
                                            {saving ? 'Saving…' : 'Save changes'}
                                        </button>
                                        <button
                                            type='button'
                                            className="ui-btn ui-btn-secondary ui-btn-block"
                                            onClick={() => {
                                                setEditMode(false);
                                                setFormData({
                                                    phone: profileData?.phone || '',
                                                    telegram: profileData?.telegram || ''
                                                });
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            ) : (
                                <>
                                    <div className="ui-field">
                                        <div className="ui-label">Phone</div>
                                        <div className="ui-readonly">{profileData?.phone || '—'}</div>
                                    </div>

                                    <div className="ui-field">
                                        <div className="ui-label">Telegram</div>
                                        <div className="ui-readonly">{profileData?.telegram ? `@${profileData.telegram}` : '—'}</div>
                                    </div>

                                    <button className="ui-btn ui-btn-primary ui-btn-block" onClick={() => setEditMode(true)}>
                                        Edit profile
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="ui-actions" style={{ justifyContent: 'center', marginTop: '1.25rem' }}>
                    <button className="ui-btn ui-btn-secondary" onClick={() => navigate('/my-rides')}>My rides</button>
                    <button className="ui-btn ui-btn-secondary" onClick={() => navigate('/home')}>Home</button>
                </div>
            </div>
            <ToastContainer />
        </div>
    );
}

export default Profile;
